import {
  classifyManagementFiltersEntitiesIds,
  createCalendarDate,
  DocumentType,
  formatCalendarMonth,
  formatEurCompact,
  formatNumberToEuroCurrency,
  getLocaleFromLanguage,
  isNilOrEmpty,
  MONTHS_IN_YEAR,
  type CalendarDate,
  type Filter,
  type FilterGroup,
  type Language,
  type RentCellStatus,
  RentExportStatus,
  RentRowKind,
  type RentFilter,
  type RentRow,
  type ScopeInput,
} from '@rentguru/commons-utils';
import { type RentMatrixMonth, type RentMatrixRow } from '@up2rent/ui-new';
import compact from 'lodash/compact';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import last from 'lodash/last';
import range from 'lodash/range';
import uniq from 'lodash/uniq';

import { type PossibleDashboardFilters } from 'src/components/Dashboard/filters/dashboardFilterUtils';
import { getClassicFilterItems } from 'src/components/Leases/Tables/leaseFiltersUtils';
import { type Permissions } from 'src/hooks/utils/PermissionsContext';
import { base64ToBytes } from 'src/utils/base64Utils';

const SERVED_FISCAL_YEARS = 3;
const COMPACT_CELL_AMOUNT_THRESHOLD = 100_000;

export const formatRentCellAmount = (amount: number, language: Language): string =>
  Math.abs(amount) >= COMPACT_CELL_AMOUNT_THRESHOLD
    ? formatEurCompact(amount, language)
    : formatNumberToEuroCurrency(amount, language);
const MIN_FISCAL_YEAR = 2000;
const YEAR_LABEL_PREFIX = "'";

export type ReminderDocumentType =
  | DocumentType.LeaseFirstReminder
  | DocumentType.LeaseSecondReminder
  | DocumentType.LeaseThirdReminder
  | DocumentType.LeaseFormalNotice;

export interface RentRowTitle {
  moreUnitCount: number;
  name: string;
  unit: string | null;
}

export interface RentRowSubtitle {
  leaseLabel: string;
  building?: string;
}

interface RentMatrixRowFormatters {
  formatCredit: (amount: number) => string;
  formatSubtitle: (subtitle: RentRowSubtitle) => string;
  formatTitle: (title: RentRowTitle) => string;
  formatUnitSubtitle: (subtitle: RentRowSubtitle) => string;
}

export const canViewRentTracking = (
  {
    financialTransactionsRead,
    leasesDetailsRead,
  }: Pick<Permissions, 'financialTransactionsRead' | 'leasesDetailsRead'>,
  isOwner: boolean
): boolean => leasesDetailsRead && financialTransactionsRead && !isOwner;

export const getRentRowTitle = ({
  leaseLabel,
  tenantName,
  units,
}: Pick<RentRow, 'leaseLabel' | 'tenantName' | 'units'>): RentRowTitle =>
  isEmpty(tenantName)
    ? { moreUnitCount: 0, name: leaseLabel, unit: null }
    : { moreUnitCount: Math.max(0, units.length - 1), name: tenantName, unit: head(units) ?? null };

export const getRentRowDisplayTitle = (
  row: Pick<RentRow, 'leaseLabel' | 'rowId' | 'rowKind' | 'tenantName' | 'units'>,
  formatTitle: (title: RentRowTitle) => string
): string => (row.rowKind === RentRowKind.UNIT ? (head(row.units) ?? row.rowId) : formatTitle(getRentRowTitle(row)));

export const toMatrixRow = (
  row: RentRow,
  { formatCredit, formatSubtitle, formatTitle, formatUnitSubtitle }: RentMatrixRowFormatters
): RentMatrixRow => {
  const creditBalance = row.tenantCreditBalance ?? 0;
  const subtitle: RentRowSubtitle = { building: head(row.buildingNames), leaseLabel: row.leaseLabel };
  const isUnitRow = row.rowKind === RentRowKind.UNIT;
  return {
    cells: row.cells,
    creditLabel: creditBalance > 0 ? formatCredit(creditBalance) : undefined,
    id: row.rowId,
    isDispute: row.isDispute,
    rowTotal: row.rowTotal,
    subtitle: isUnitRow ? formatUnitSubtitle(subtitle) : formatSubtitle(subtitle),
    title: getRentRowDisplayTitle(row, formatTitle),
  };
};

export const currentFiscalYear = (at: CalendarDate): number => at.year;

export const earliestFiscalYear = (serverMinFiscalYear: number | null | undefined, at: CalendarDate): number =>
  Math.max(MIN_FISCAL_YEAR, Math.min(at.year - SERVED_FISCAL_YEARS + 1, serverMinFiscalYear ?? at.year));

export const fiscalYearMonths = (fiscalYear: number, language: Language): RentMatrixMonth[] => {
  const localeCode = getLocaleFromLanguage(language).code;
  const twoDigitYear = createCalendarDate(fiscalYear, 1, 1).toLocaleString(localeCode, { year: '2-digit' });
  const yearLabel = `${YEAR_LABEL_PREFIX}${twoDigitYear}`;
  return range(1, MONTHS_IN_YEAR + 1).map((month) => ({
    index: month,
    key: `${fiscalYear}-${month}`,
    label: formatCalendarMonth(createCalendarDate(fiscalYear, month, 1), language, 'short'),
    year: fiscalYear,
    yearLabel,
  }));
};

export const formatMonthRange = (months: RentMatrixMonth[]): string => {
  const firstMonth = head(months);
  const lastMonth = last(months);
  return isNil(firstMonth) || isNil(lastMonth) ? '' : `${firstMonth.label}–${lastMonth.label}`;
};

export const gunzipBase64 = (content: string, type: string): Promise<Blob> =>
  new Response(new Blob([base64ToBytes(content)]).stream().pipeThrough(new DecompressionStream('gzip')), {
    headers: { 'Content-Type': type },
  }).blob();

const RENT_EXPORT_NOTICE_IDS: Record<RentExportStatus, string | null> = {
  [RentExportStatus.PREPARING]: 'dashboard.widgets.rent.exportPreparing',
  [RentExportStatus.READY]: null,
  [RentExportStatus.TOO_LARGE]: 'dashboard.widgets.rent.exportTooLarge',
};

export const getRentExportNoticeId = (status: RentExportStatus): string | null => RENT_EXPORT_NOTICE_IDS[status];

export const buildScopeInput = (managementFilterGroups: FilterGroup[]): ScopeInput => {
  const { buildingIds, ownerIds } = classifyManagementFiltersEntitiesIds(managementFilterGroups);
  return {
    managerIds: managementFilterGroups.reduce<string[]>(
      (acc, { userId }) => (isNilOrEmpty(userId) || acc.includes(userId) ? acc : [...acc, userId]),
      []
    ),
    ownerIds: uniq(ownerIds),
    propertyIds: uniq(buildingIds),
  };
};

export interface RentFilterDimensions {
  buildingIds: string[];
  cities: string[];
  ownerIds: string[];
  unitIds: string[];
}

export interface RentTrackingFilterState extends RentFilterDimensions {
  statuses: RentCellStatus[];
  withLeaseOnly: boolean;
}

interface RentFilterState extends RentTrackingFilterState {
  managementFilterGroups: FilterGroup[];
  search: string;
}

const DASHBOARD_FILTER_BY_DIMENSION: Record<keyof RentFilterDimensions, PossibleDashboardFilters> = {
  buildingIds: 'building',
  cities: 'city',
  ownerIds: 'owner',
  unitIds: 'unit',
};

export const toRentFilterDimensions = (dashboardFilters: Filter[]): RentFilterDimensions => ({
  buildingIds: getClassicFilterItems(dashboardFilters, DASHBOARD_FILTER_BY_DIMENSION.buildingIds),
  cities: getClassicFilterItems(dashboardFilters, DASHBOARD_FILTER_BY_DIMENSION.cities),
  ownerIds: getClassicFilterItems(dashboardFilters, DASHBOARD_FILTER_BY_DIMENSION.ownerIds),
  unitIds: getClassicFilterItems(dashboardFilters, DASHBOARD_FILTER_BY_DIMENSION.unitIds),
});

export const toRentFilter = (state: RentFilterState): RentFilter | null => {
  const { unitIds } = classifyManagementFiltersEntitiesIds(state.managementFilterGroups);
  const filter: RentFilter = {
    buildingIds: state.buildingIds,
    cities: state.cities,
    ownerIds: state.ownerIds,
    search: state.search,
    selectedUnitIds: state.unitIds,
    statuses: state.statuses,
    unitIds: uniq(unitIds),
    withLeaseOnly: state.withLeaseOnly,
  };
  const isEmptyFilter =
    isEmpty(filter.buildingIds) &&
    isEmpty(filter.cities) &&
    isEmpty(filter.ownerIds) &&
    isEmpty(filter.search) &&
    isEmpty(filter.selectedUnitIds) &&
    isEmpty(filter.statuses) &&
    isEmpty(filter.unitIds) &&
    !state.withLeaseOnly;
  return isEmptyFilter ? null : filter;
};

export const countActiveRentFilters = (filters: RentTrackingFilterState, search: string): number =>
  filters.buildingIds.length +
  filters.cities.length +
  filters.ownerIds.length +
  filters.statuses.length +
  filters.unitIds.length +
  (filters.withLeaseOnly ? 1 : 0) +
  (isEmpty(search) ? 0 : 1);

export const reminderDocumentTypes = (thirdReminderEnabled: boolean): ReminderDocumentType[] =>
  compact([
    DocumentType.LeaseFirstReminder,
    DocumentType.LeaseSecondReminder,
    thirdReminderEnabled ? DocumentType.LeaseThirdReminder : null,
    DocumentType.LeaseFormalNotice,
  ]);

export const EMPTY_RENT_FILTERS: RentTrackingFilterState = {
  buildingIds: [],
  cities: [],
  ownerIds: [],
  statuses: [],
  unitIds: [],
  withLeaseOnly: false,
};

export interface RentTrackingLocationState {
  rentFilters: RentFilterDimensions;
}
